import React from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from '../../i18n/useTranslation';

const wrapperStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'stretch',
    height: 28,
    border: '1px solid #ccc',
    borderRadius: 6,
    overflow: 'hidden',
};

const warningWrapperStyle: React.CSSProperties = {
    ...wrapperStyle,
    border: '1px solid #d32f2f',
};

const buttonStyle: React.CSSProperties = {
    border: 'none',
    backgroundColor: '#f9f9f9',
    color: '#333',
    width: 22,
    height: '100%',
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const disabledButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    color: '#bbb',
    cursor: 'not-allowed',
};

const inputStyle: React.CSSProperties = {
    width: 56,
    height: '100%',
    textAlign: 'center' as const,
    fontSize: 13,
    border: 'none',
    borderLeft: '1px solid #ccc',
    borderRight: '1px solid #ccc',
    outline: 'none',
};

// Not a business rule - JS numbers (and the backend's double) lose integer precision beyond this,
// so anything past it is a technical safety ceiling, not an order-quantity limit.
const MAX_SAFE_QUANTITY = Number.MAX_SAFE_INTEGER;

const QuantityInput = ({
    quantity,
    minQuantity,
    step = 1,
    warningMessage,
    onChange,
}: {
    quantity: number;
    minQuantity: number;
    step?: number;
    warningMessage?: string;
    onChange: (newQuantity: number) => void;
}) => {
    const { t } = useTranslation('translation');
    const isAtMinQuantity = quantity <= minQuantity;
    const hasWarning = !!warningMessage;

    const nearestValidQuantity = (value: number): number => {
        if (value <= minQuantity) {
            return minQuantity;
        }
        if (value >= MAX_SAFE_QUANTITY) {
            return MAX_SAFE_QUANTITY;
        }
        const stepsFromMin = Math.round((value - minQuantity) / step);
        return minQuantity + stepsFromMin * step;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.trim();
        if (!/^\d+$/.test(raw)) {
            return;
        }
        onChange(Math.min(Number(raw), MAX_SAFE_QUANTITY));
    };

    const handleInputBlur = () => {
        const corrected = nearestValidQuantity(quantity);
        if (corrected !== quantity) {
            onChange(corrected);
        }
    };

    const increment = () => onChange(Math.min(quantity + step, MAX_SAFE_QUANTITY));

    const decrement = () => {
        onChange(Math.max(quantity - step, minQuantity));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            increment();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            decrement();
        }
    };

    return (
        <div style={hasWarning ? warningWrapperStyle : wrapperStyle}>
            <Tooltip title={isAtMinQuantity ? t('MIN_QUANTITY_REACHED') : ''}>
                <span>
                    <button
                        type="button"
                        style={isAtMinQuantity ? disabledButtonStyle : buttonStyle}
                        onClick={decrement}
                        disabled={isAtMinQuantity}
                        aria-label="decrement"
                    >
                        −
                    </button>
                </span>
            </Tooltip>
            <Tooltip title={warningMessage ?? ''}>
                <input
                    value={quantity}
                    min={minQuantity}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    style={inputStyle}
                    aria-label="quantity input"
                />
            </Tooltip>
            <button type="button" style={buttonStyle} onClick={increment} aria-label="increment">
                +
            </button>
        </div>
    );
};

export default QuantityInput;
